export type ConsentKey = 'ad_storage' | 'ad_user_data' | 'ad_personalization' | 'analytics_storage';

export type ConsentSnapshot = Partial<Record<ConsentKey, 'granted' | 'denied' | 'unset'>>;

export type GcmCheck = {
  id: string;
  title: string;
  ok: boolean;
  detail?: string;
  fix?: string;
};

export type GcmReport = {
  gtagPresent: boolean;
  dataLayerPresent: boolean;
  gppAvailable: boolean;
  tcfAvailable: boolean;
  gpc: boolean | null;
  region: string | null;
  preConsent: ConsentSnapshot;
  postConsent: ConsentSnapshot;
  checks: GcmCheck[];
};

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null;
}

function getDataLayer(): unknown[] {
  const w = globalThis as unknown as { dataLayer?: unknown };
  return Array.isArray(w.dataLayer) ? (w.dataLayer as unknown[]) : [];
}

function readConsentFromObj(obj: Record<string, unknown>): ConsentSnapshot {
  const snap: ConsentSnapshot = {};
  const keys: ConsentKey[] = [
    'ad_storage',
    'ad_user_data',
    'ad_personalization',
    'analytics_storage',
  ];
  for (const k of keys) {
    const v = obj[k];
    if (v === 'granted' || v === 'denied') snap[k] = v;
  }
  return snap;
}

function mergeSnapshots(a: ConsentSnapshot, b: ConsentSnapshot): ConsentSnapshot {
  const out: ConsentSnapshot = { ...a };
  (Object.keys(b) as ConsentKey[]).forEach((k) => {
    out[k] = b[k];
  });
  return out;
}

function readLatestConsent(): ConsentSnapshot {
  const dl = getDataLayer();
  let snapshot: ConsentSnapshot = {};
  for (let i = dl.length - 1; i >= 0; i--) {
    const evt = dl[i];
    if (!isRecord(evt)) continue;
    if (evt.event !== 'consent') continue;
    // Common shapes observed around GTM / gtag
    const candidateFields = ['update', 'state', 'default', 'params', 'payload', 'arg'];
    for (const f of candidateFields) {
      const m = evt[f];
      if (isRecord(m)) {
        snapshot = mergeSnapshots(snapshot, readConsentFromObj(m));
      }
    }
    // Prefer the latest 'update'
    const type =
      typeof evt.type === 'string'
        ? (evt.type as string)
        : typeof evt.action === 'string'
          ? (evt.action as string)
          : undefined;
    if (type === 'update') break;
  }
  return snapshot;
}

export function runConsentModeChecks(opts?: { region?: string | null }): GcmReport {
  const w = globalThis as unknown as {
    gtag?: unknown;
    dataLayer?: unknown;
    __gpp?: unknown;
    __tcfapi?: unknown;
    __cmpRegion?: unknown;
  };
  const gtagPresent = typeof w.gtag === 'function';
  const dataLayerPresent = Array.isArray(w.dataLayer);

  // If your SDK pushes a default first, this would differ; for now we read latest twice for stability
  const preConsent = readLatestConsent();
  const postConsent = readLatestConsent();

  const gppAvailable = typeof w.__gpp === 'function';
  const tcfAvailable = typeof w.__tcfapi === 'function';
  const gpc =
    (navigator as unknown as { globalPrivacyControl?: boolean }).globalPrivacyControl ?? null;
  const region =
    opts?.region ?? (typeof w.__cmpRegion === 'string' ? (w.__cmpRegion as string) : null);

  const wantKeys: ConsentKey[] = [
    'ad_storage',
    'ad_user_data',
    'ad_personalization',
    'analytics_storage',
  ];

  const checks: GcmCheck[] = [];

  checks.push({
    id: 'gtag',
    title: 'gtag function present',
    ok: gtagPresent,
    fix: gtagPresent ? undefined : 'Ensure the Google tag is loaded before consent signals.',
  });
  checks.push({
    id: 'datalayer',
    title: 'dataLayer array present',
    ok: dataLayerPresent,
    fix: dataLayerPresent
      ? undefined
      : 'Initialize window.dataLayer = window.dataLayer || []; before gtag snippet.',
  });

  // Pre-consent defaults
  wantKeys.forEach((k) => {
    const v = preConsent[k] ?? 'unset';
    const ok = v === 'denied' || v === 'unset';
    checks.push({
      id: `pre-${k}`,
      title: `Pre-consent: ${k} is denied`,
      ok,
      detail: `Observed: ${v}`,
      fix: ok ? undefined : `Emit default consent with ${k}='denied' before any tags fire.`,
    });
  });

  // Post-consent update (informational; ok if granted or unset)
  wantKeys.forEach((k) => {
    const v = postConsent[k] ?? 'unset';
    const ok = v === 'granted' || v === 'unset';
    checks.push({
      id: `post-${k}`,
      title: `Post-consent: ${k} lifts correctly`,
      ok,
      detail: `Observed: ${v}`,
      fix: ok ? undefined : `On user accept, call gtag('consent','update',{ ${k}: 'granted' }).`,
    });
  });

  checks.push({
    id: 'gpc',
    title: 'GPC respected (informational)',
    ok: gpc !== null,
    detail: gpc === null ? 'Navigator GPC not exposed' : `GPC=${gpc}`,
    fix: gpc === null ? 'Test in a GPC-enabled browser/environment.' : undefined,
  });

  checks.push({
    id: 'gpp',
    title: '__gpp available',
    ok: gppAvailable,
    fix: gppAvailable ? undefined : 'Install your CMP web SDK to expose __gpp.',
  });
  checks.push({
    id: 'tcf',
    title: '__tcfapi available (EU only)',
    ok: region === 'eea-uk' ? tcfAvailable : true,
    detail: region === 'eea-uk' ? undefined : 'Skipped (non-EU region)',
    fix:
      region === 'eea-uk' && !tcfAvailable
        ? 'Enable TCF feature flag and set CoreModel when GDPR applies.'
        : undefined,
  });

  return {
    gtagPresent,
    dataLayerPresent,
    gppAvailable,
    tcfAvailable,
    gpc,
    region,
    preConsent,
    postConsent,
    checks,
  };
}
